'use client';

import Link from 'next/link';
import { Article } from './types';
import clsx from 'clsx';
import Authors from './authors';
import { formatDateToLocal } from '@/lib/utils/utils';
import Icon from '@/app/components/ui/common/icon-component';
import { useGSAP } from '@gsap/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { useTranslation } from 'react-i18next';
import { urlForImage } from '@/sanity/lib/image';
import { breakpoints } from '@/app/config/breakpoints';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(useGSAP);
}

interface ArticleCardProps {
    article: Article; // 文章数据
    maxLine?: 2 | 3;
    icon?: React.ReactNode;
    iconTitle?: string;
    imageVsContent?: { image: number; content: number };
    showDescription?: boolean;
    layout?: 'vertical' | 'horizontal';
    locale?: string;
    imageFitMode?: 'fill-width' | 'fill-height'; // 图片填充模式：fill-width=宽度100%高度裁剪, fill-height=高度100%宽度裁剪
    disableImageRound?: boolean;
    cardSize?: 'small' | 'large';
    showArticleType?: boolean;
    contentVerticalCenter?: boolean;
    isTop?: boolean;
}

const defaultImageVsContent = {
    image: 220,
    content: 160,
};

export default function ArticleCard({
    article,
    icon,
    iconTitle,
    imageVsContent = defaultImageVsContent,
    maxLine = 2,
    showDescription = true,
    layout = 'vertical',
    locale = 'en',
    imageFitMode = 'fill-width', // 默认宽度填充
    cardSize = 'small',
    disableImageRound = false,
    showArticleType = false,
    contentVerticalCenter = false,
    isTop = false,
}: ArticleCardProps) {
    const { t } = useTranslation('blog');
    const cardRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const hoverTweenRef = useRef<gsap.core.Timeline | gsap.core.Tween | null>(
        null,
    );
    const [canHover, setCanHover] = useState(false);

    const articleType = article.type;
    const articleTypeIconName = {
        weekly: 'weekly-orange',
        news: 'news-green',
        research: 'research-blue',
    }[article.type];
    const clampLine = maxLine;
    const titleBlockHeight = `calc(1.4em * ${clampLine})`;
    const titleUnderlineColor =
        articleType === 'weekly' || isTop ? '#FFFFFF' : '#0A0708';
    const href = `/${locale}/blog${article.slug}`;
    const imageQuality = isTop ? 68 : articleType === 'news' ? 60 : 62;
    const tabletImageQuality = Math.min(imageQuality + 4, 72);

    // 服务端裁剪尺寸：按卡片固定比例裁好，移动端少传无效像素
    // fit='crop' 会尊重 Sanity 图片的 hotspot/crop 字段
    const mobileImageCropOptions: {
        width: number;
        height?: number;
        fit?: 'crop' | 'max';
    } = isTop
        ? { width: 900, height: 406, fit: 'crop' } // horizontal 大卡，比例约 82:37
        : imageFitMode === 'fill-height'
          ? { width: 560, height: 333, fit: 'crop' } // 竖卡 37:22
          : { width: 640, height: 289, fit: 'crop' }; // 普通横卡 82:37
    const tabletImageCropOptions: {
        width: number;
        height?: number;
        fit?: 'crop' | 'max';
    } = isTop
        ? { width: 1120, height: 505, fit: 'crop' }
        : imageFitMode === 'fill-height'
          ? { width: 720, height: 428, fit: 'crop' } // 竖卡 37:22
          : { width: 780, height: 352, fit: 'crop' }; // 普通横卡 82:37

    const mobileImageUrl = urlForImage(article.image, {
        quality: imageQuality,
        ...mobileImageCropOptions,
    });
    const tabletImageUrl = urlForImage(article.image, {
        quality: tabletImageQuality,
        ...tabletImageCropOptions,
    });
    const backgroundImageUrl = urlForImage(article.image, {
        quality: 40,
        width: isTop ? 320 : 240,
    });
    const [imageLoaded, setImageLoaded] = useState(!mobileImageUrl);

    useEffect(() => {
        setImageLoaded(!mobileImageUrl);
    }, [mobileImageUrl, tabletImageUrl]);

    useEffect(() => {
        const image = imageRef.current;
        if (!image) {
            return;
        }

        if (image.complete && image.naturalWidth > 0) {
            setImageLoaded(true);
        }
    }, [mobileImageUrl, tabletImageUrl]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const mediaQuery = window.matchMedia(
            `(min-width: ${breakpoints.tablet}px) and (hover: hover) and (pointer: fine)`,
        );
        const updateCanHover = () => {
            setCanHover(mediaQuery.matches);
        };

        updateCanHover();
        mediaQuery.addEventListener('change', updateCanHover);

        return () => {
            mediaQuery.removeEventListener('change', updateCanHover);
        };
    }, []);

    // 把标题拆成字符数组，空格转成 \u00A0，避免折叠
    const titleChars = useMemo(
        () =>
            Array.from(article.title).map((ch, idx) => ({
                key: `char-${idx}`,
                text: ch,
            })),
        [article.title],
    );

    // 初始化：把所有字符 span 的 opacity 设为 0
    useGSAP(
        () => {
            if (!canHover) {
                const chars = gsap.utils.toArray<HTMLSpanElement>(
                    '.title-underline-char',
                );

                if (chars.length) {
                    gsap.set(chars, {
                        boxShadow: 'inset 0 -1.5px 0 0 transparent',
                    });
                }

                return;
            }

            const chars = gsap.utils.toArray<HTMLSpanElement>(
                '.title-underline-char',
            );
            if (!chars.length) return;

            gsap.set(chars, {
                boxShadow: 'inset 0 -1.5px 0 0 transparent',
            });
        },
        { dependencies: [canHover], scope: cardRef },
    );

    const handleMouseEnter = () => {
        if (!canHover) {
            return;
        }

        const chars = gsap.utils.toArray<HTMLSpanElement>(
            cardRef.current?.querySelectorAll('.title-underline-char') || [],
        );
        if (!chars.length) return;

        hoverTweenRef.current?.kill();

        hoverTweenRef.current = gsap.to(chars, {
            boxShadow: `inset 0 -1.5px 0 0 ${titleUnderlineColor}`,
            duration: 0.01,
            stagger: {
                each: 0.01, // 逐字符
                from: 0, // 左 → 右
            },
            ease: 'power2.out',
        });
    };

    const handleMouseLeave = () => {
        if (!canHover) {
            return;
        }

        const chars = gsap.utils.toArray<HTMLSpanElement>(
            cardRef.current?.querySelectorAll('.title-underline-char') || [],
        );
        if (!chars.length) return;

        hoverTweenRef.current?.kill();

        hoverTweenRef.current = gsap.to(chars, {
            boxShadow: 'inset 0 -1.5px 0 0 transparent',
            duration: 0.01,
            stagger: {
                each: 0.01,
                from: 'end', // 右 → 左
            },
            ease: 'power2.in',
        });
    };

    // 文字区域
    const renderContent = () => (
        <div
            className={clsx('relative h-full w-full overflow-hidden', {
                'text-[#FFFFFF]': articleType === 'weekly' || isTop,
                'bg-white text-[#0A0708]': articleType !== 'weekly',
                'tablet:rounded-br-[12px] desktop:rounded-br-[16px] rounded-br-[8px]':
                    !disableImageRound,
                'tablet:rounded-bl-[12px] desktop:rounded-bl-[16px] rounded-bl-[8px]':
                    layout === 'vertical' && !disableImageRound,
                'tablet:rounded-tr-[12px] desktop:rounded-tr-[16px] rounded-tr-[8px]':
                    layout === 'horizontal' && !disableImageRound,
            })}
        >
            {/* s3样式：使用伪元素放置模糊背景图 */}
            {(articleType === 'weekly' || isTop) && backgroundImageUrl && (
                <>
                    {!imageLoaded && (
                        <div className="absolute inset-0 z-0 bg-[#F1F1F1]" />
                    )}
                    {imageLoaded && (
                        <>
                            <div
                                className="absolute inset-0 z-0"
                                style={{
                                    backgroundImage: `url(${backgroundImageUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    filter: 'blur(12px) brightness(0.7)',
                                    // 扩展背景避免模糊边缘出现白边
                                    transform: 'scale(1.1)',
                                }}
                            />
                            {/* 半透明遮罩 */}
                            <div className="absolute inset-0 z-0 bg-white/10" />
                        </>
                    )}
                </>
            )}
            <div
                className={clsx('relative z-10 flex h-full w-full flex-col', {
                    'justify-center': contentVerticalCenter,
                    'gap-[8px]': layout === 'vertical',
                    'gap-[6px]': layout === 'horizontal',
                    'tablet:p-[20px] desktop:p-[24px] j-h5 p-[16px]':
                        cardSize === 'large' && articleType !== 'news',
                    'tablet:p-[20px] desktop:p-[24px] j-h6 p-[16px]':
                        cardSize === 'small' && articleType !== 'news',
                    'tablet:p-[12px] desktop:p-[16px] j-h7 p-[8px]':
                        articleType === 'news',
                })}
            >
                {showArticleType && (
                    <div className="tablet:mb-[6px] mb-[4px] flex items-center gap-[4px]">
                        <Icon
                            name={articleTypeIconName}
                            className="inline-block h-[21px] w-[21px]"
                        />
                        <p
                            className={clsx('j-l1 capitalize', {
                                'text-[#FFB039]': article.type === 'weekly',
                                'text-[#1AFF21]': article.type === 'news',
                                'text-[#2F9BFF]': article.type === 'research',
                            })}
                        >
                            {t('sections.' + article.type + '.title')}
                        </p>
                    </div>
                )}

                <div
                    className="j-h7 relative flex w-full flex-col"
                    style={{ height: titleBlockHeight }}
                >
                    <div
                        className={clsx('w-full break-words', {
                            'line-clamp-2': clampLine === 2,
                            'line-clamp-3': clampLine === 3,
                        })}
                    >
                        {titleChars.map(({ key, text }) => (
                            <span
                                key={key}
                                className="title-underline-char"
                                style={{
                                    lineHeight: 'inherit',
                                    boxShadow: 'inset 0 -1.5px 0 0 transparent',
                                }}
                            >
                                {text}
                            </span>
                        ))}
                    </div>

                    {showDescription && (
                        <p className="j-t3 mb-3 break-words text-sm text-[#9B9A9D]">
                            {article.description}
                        </p>
                    )}
                </div>

                <div
                    className={clsx(
                        'j-t2 tablet:mt-[16px] desktop:mt-[16px] mt-[12px] flex w-full items-end',
                        {
                            'text-[#DBDBDB]': articleType === 'weekly',
                            'text-[#9B9A9D]': articleType !== 'weekly',
                        },
                    )}
                >
                    {article.type === 'research' && (
                        <div className="min-w-0 overflow-hidden">
                            <Authors
                                authors={article.authors}
                                locale={locale}
                            />
                        </div>
                    )}
                    <div className={clsx('flex-none')}>
                        {article.type === 'research' && <span>/&nbsp;</span>}
                        {article.type !== 'weekly' &&
                            formatDateToLocal(article.publishedAt, locale)}
                        {article.type === 'weekly' && (
                            <span>
                                {formatDateToLocal(article.publishedAt, locale)}{' '}
                                | {article.issueNumber}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // 图片区域
    const renderImage = () => (
        <div
            className={clsx('relative flex w-full overflow-hidden', {
                'tablet:rounded-b-[12px] desktop:rounded-b-[16px] rounded-b-[8px]':
                    articleType === 'news' && !isTop,
                'tablet:rounded-tl-[12px] desktop:rounded-tl-[16px] rounded-tl-[8px]':
                    !disableImageRound,
                'tablet:rounded-tr-[12px] desktop:rounded-tr-[16px] rounded-tr-[8px]':
                    layout === 'vertical' && !disableImageRound,
                'tablet:rounded-bl-[12px] desktop:rounded-bl-[16px] rounded-bl-[8px]':
                    layout === 'horizontal' && !disableImageRound,
                // fill-width: 宽度填充，容器按宽高比自动计算高度
                'aspect-[82/37]': imageFitMode === 'fill-width',
                // fill-height: 高度填充，使用固定高度作为基准
                'aspect-[37/22]':
                    imageFitMode === 'fill-height' && layout === 'vertical',
                'h-full':
                    imageFitMode === 'fill-height' && layout === 'horizontal',
            })}
        >
            {mobileImageUrl ? (
                <>
                    <div
                        className={clsx(
                            'absolute inset-0 z-10 bg-[#F1F1F1]',
                            'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent',
                            imageLoaded
                                ? 'pointer-events-none opacity-0'
                                : 'opacity-100',
                        )}
                    />
                    <picture className="absolute inset-0 block h-full w-full">
                        <source
                            media={`(min-width: ${breakpoints.tablet}px)`}
                            srcSet={tabletImageUrl}
                        />
                        <img
                            ref={imageRef}
                            src={mobileImageUrl}
                            alt={article.title}
                            loading={isTop ? 'eager' : 'lazy'}
                            decoding="async"
                            onLoad={() => setImageLoaded(true)}
                            onError={() => setImageLoaded(true)}
                            className={clsx(
                                'h-full w-full object-cover object-center transition-transform duration-500 ease-out',
                                imageLoaded ? 'opacity-100' : 'opacity-0',
                                {
                                    'group-hover:scale-[1.03]':
                                        articleType === 'news',
                                    'group-hover:translate-y-[4px] group-hover:scale-[1.08]':
                                        articleType !== 'news',
                                },
                            )}
                        />
                    </picture>
                </>
            ) : null}
        </div>
    );

    return (
        <Link
            href={href}
            className="block h-full w-full self-start"
            target="_blank"
        >
            <div
                ref={cardRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={clsx('group grid h-full w-full overflow-hidden', {
                    'grid-cols-1': layout === 'vertical',
                    'grid-cols-2': layout === 'horizontal',
                    'tablet:rounded-[12px] desktop:rounded-[16px] rounded-[8px] border border-[#E8E8E8]':
                        articleType === 'research',
                    'border-0': articleType !== 'research',
                })}
            >
                <div className={clsx('col-span-1 h-full w-full')}>
                    {renderImage()}
                </div>
                <div className={clsx('col-span-1 h-full w-full')}>
                    {renderContent()}
                </div>
            </div>
        </Link>
    );
}
